import { useCallback } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { MovieWatchParamsFragment } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import {
    Track,
    useAudioTrackContext,
} from '@/scenarios/legacy-player/components/PlayerSkin/parts/Tracks/Tracks.types';
import {
    useGroupBy,
    useTrackMapTransform,
} from '@/scenarios/legacy-player/components/PlayerSkin/parts/Tracks/hooks/useAllAudioTracks';
import { SoundTracksListProps } from '@/scenarios/legacy-player/components/PlayerSkin/parts/SoundTracksList/SoundTrackList.types';
import { trackKey } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Tracks/SoundQuality/useSoundQualityTracks';

const MAX_DEFAULT_VISIBLE_SOUNDTRACKS = 3;

const showAllTracks = (tracksCount: number) => tracksCount <= MAX_DEFAULT_VISIBLE_SOUNDTRACKS;

const makeTrackName = (intl: IntlShape, t: Track): string => {
    const { languageName, studioName, isForAdult } = t.info ?? {};
    return `${
        t.info?.languageName
            ? intl.formatMessage(
                  {
                      id: 'components.PlayerSkin.0efeabf8713ffde731431653eb1972da',
                      defaultMessage:
                          '{languageName}{studioName, select, undefined {} other { · }}{studioName}{isForAdult, select, true { · 18+} other {}}',
                      description: 'Название аудиодорожки в формате Язык  · Студия ·  18+',
                  },
                  {
                      languageName,
                      studioName,
                      isForAdult,
                  }
              )
            : t.name ||
              intl.formatMessage({
                  id: 'components.PlayerSkin.88183b946cc5f0e8c96b2e66e1c74a7e',
                  defaultMessage: 'Unknown',
                  description: 'Текст для неизвестного трека в списке озвучек',
              })
    }`;
};

const compareTracks =
    (
        watchParams: MovieWatchParamsFragment['watchParams'] | undefined,
        tracksGrouped: Map<string, Track[]>
    ) =>
    (t1: Track, t2: Track) => {
        if (
            tracksGrouped
                .get(trackKey(t1))
                ?.some((track) => track.lang === watchParams?.audioLanguage)
        ) {
            return -1;
        }
        if (
            tracksGrouped
                .get(trackKey(t2))
                ?.some((track) => track.lang === watchParams?.audioLanguage)
        ) {
            return 1;
        }
        if (t1.info?.index !== undefined && t2.info?.index !== undefined) {
            return t1.info.index - t2.info.index;
        }
        return t1.info?.index === undefined ? 1 : -1;
    };

const checkShow: (
    track: Track,
    watchParams: MovieWatchParamsFragment['watchParams'] | undefined
) => boolean = (track, watchParams) =>
    track.info === undefined ||
    track.info.visibleByDefault ||
    track.lang === watchParams?.audioLanguage;

interface Params {
    onSelectAudioTrack: (track: Track) => void;
    isAllShown: boolean;
}

export const useAudioTracks: ({ onSelectAudioTrack, isAllShown }: Params) => {
    currentTrack: Track | undefined;
    soundTracks: readonly Track[];
    handleSelectSoundTrack: SoundTracksListProps['handleSelectSoundTrack'];
    hasInvisibleByDefaultTracks: boolean;
} = ({ onSelectAudioTrack, isAllShown }) => {
    const {
        currentAudioTrack: currentAudioTrackId,
        allAudioTracksMap: allAudioTracksMapRaw,
        watchParams,
    } = useAudioTrackContext();

    const tracksGrouped = useGroupBy(allAudioTracksMapRaw, trackKey);

    const isAllStereo = Array.from(tracksGrouped).every((value) => value[1].length === 1);
    const intl = useIntl();

    const allAudioTracksMap = useTrackMapTransform(allAudioTracksMapRaw, (track: Track) => ({
        ...track,
        name: makeTrackName(intl, track),
        note:
            !isAllStereo && tracksGrouped.get(trackKey(track))!.length === 1
                ? intl.formatMessage({
                      id: 'components.PlayerSkin.478018844c33aeacc2fd555e1cfee730',
                      defaultMessage: 'Only stereo',
                      description: 'Текст для озвучки, которая доступна только в стерео',
                  })
                : undefined,
    }));

    const map = useGroupBy(allAudioTracksMap, (track) => track.info?.quality ?? 'OTHER');

    const currentSoundTrack = currentAudioTrackId
        ? allAudioTracksMap.get(currentAudioTrackId)
        : undefined;

    let soundTracks =
        currentAudioTrackId !== undefined
            ? map.get(allAudioTracksMap.get(currentAudioTrackId)!.info?.quality ?? 'OTHER')!
            : [];

    soundTracks.sort(compareTracks(watchParams, tracksGrouped));

    const hasInvisibleByDefaultTracks =
        !showAllTracks(soundTracks.length) &&
        soundTracks.some((track) => !checkShow(track, watchParams));

    if (!isAllShown && !showAllTracks(soundTracks.length)) {
        soundTracks = soundTracks.filter((track) => checkShow(track, watchParams));
    }

    const handlerWrapper = useCallback<SoundTracksListProps['handleSelectSoundTrack']>(
        (track) => {
            onSelectAudioTrack(
                Array.from(allAudioTracksMapRaw.values()).find(
                    (t: Track) => t.value === track.value
                )!
            );
        },
        [allAudioTracksMapRaw, onSelectAudioTrack]
    );

    return {
        currentTrack: currentSoundTrack,
        soundTracks,
        handleSelectSoundTrack: handlerWrapper,
        hasInvisibleByDefaultTracks,
    };
};
